import React, { useState, useEffect, useRef } from 'react';
import { Archive, Download, RotateCcw, Trash2, Upload } from 'lucide-react';
import adminApi, { getAdminUsername } from '../adminApi';
import { useLanguage } from '../context/LanguageContext';
import { useDialogs } from '../context/DialogContext';
import './Backups.css';

// ---- Types ----
interface Backup {
  id: number;
  file_path: string;
  database_type: string;
  size_bytes: number;
  created_at: string;
  created_by: string;
}

const Spinner = () => <span className="backup-spinner" aria-hidden="true"></span>;

function Backups() {
  const { t } = useLanguage();
  const { notify, confirm } = useDialogs();
  const [backups, setBackups] = useState<Backup[]>([]);
  const [selectedBackupId, setSelectedBackupId] = useState<number | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Lista kopii raz, przy wejściu do widoku
  useEffect(() => {
    fetchBackups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Zapobiegaj otwarciu pliku w karcie przeglądarki po upuszczeniu poza dedykowany obszar
  useEffect(() => {
    const preventFileDrop = (event: DragEvent) => {
      if (event.dataTransfer?.types.includes("Files")) event.preventDefault();
    };
    window.addEventListener("dragover", preventFileDrop);
    window.addEventListener("drop", preventFileDrop);
    return () => {
      window.removeEventListener("dragover", preventFileDrop);
      window.removeEventListener("drop", preventFileDrop);
    };
  }, []);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/backups');
      setBackups(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching backups:', err);
      setError(t('Failed to load backups'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(undefined, {
      dateStyle: 'long',
      timeStyle: 'short',
    });
  };

  const typeLabel = (type: string) => (type === 'mongodb' ? 'MongoDB' : type === 'sqlite' ? 'SQLite' : type.toUpperCase());

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Jedna kopia obejmuje SQLite (cenniki, dane panelu) i MongoDB (konfiguracje, koszyki) -
  // na liście dwa wiersze, przywracane osobno. PostgreSQL nie jest używany (patrz CLAUDE.md)
  const handleCreateBackup = async () => {
    setIsCreating(true);
    try {
      const response = await adminApi.post('/backups', {
        database_type: 'all',
        created_by: getAdminUsername()
      });

      const failed: { database_type: string; error: string }[] = response.data.errors || [];
      if (failed.length > 0) {
        notify(`${t("Backup partly failed")}: ${failed.map((f) => `${typeLabel(f.database_type)} – ${f.error}`).join('; ')}`, "error");
      } else {
        notify(t("Backup created successfully!"), "success");
      }
      await fetchBackups();
    } catch (err) {
      console.error('Error creating backup:', err);
      notify(t("Failed to create backup"), "error");
    } finally {
      setIsCreating(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedBackupId) return;

    const toRestore = backups.find((b) => b.id === selectedBackupId);
    const confirmation = await confirm({
      message: toRestore?.database_type === 'mongodb'
        ? t("Are you sure you want to restore this MongoDB backup? All current configurations and carts will be overwritten!")
        : t("Are you sure you want to restore this backup? All current data will be overwritten!"),
      confirmLabel: t("Restore"),
      danger: true,
    });

    if (confirmation) {
      setIsRestoring(true);
      try {
        const response = await adminApi.post(
          `/backups/${selectedBackupId}/restore`
        );

        if (response.data.success) {
          notify(t("Database successfully restored!"), "success");
          setSelectedBackupId(null);
          await fetchBackups();
        }
      } catch (err) {
        console.error('Error restoring backup:', err);
        notify(t("Failed to restore backup"), "error");
      } finally {
        setIsRestoring(false);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({ message: t("Are you sure you want to delete this backup?"), confirmLabel: t("Delete"), danger: true }))) return;

    try {
      await adminApi.delete(`/backups/${id}`);
      if (selectedBackupId === id) setSelectedBackupId(null);
      notify(t("Backup deleted successfully!"), "success");
      await fetchBackups();
    } catch (err) {
      console.error('Error deleting backup:', err);
      notify(t("Failed to delete backup"), "error");
    }
  };

  const handleUploadFile = async (file: File) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('backup', file);

      const response = await adminApi.post('/backups/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const newBackup = response.data.backup;
      notify(t("Backup uploaded successfully!"), "success");
      await fetchBackups();
      setSelectedBackupId(newBackup.id);

      // Zapytaj o natychmiastowe przywrócenie
      const confirmation = await confirm({
        message: newBackup.database_type === 'mongodb'
          ? t("Backup uploaded successfully! Do you want to restore this MongoDB backup now? All current configurations and carts will be overwritten!")
          : t("Backup uploaded successfully! Do you want to restore this backup now? All current data will be overwritten!"),
        confirmLabel: t("Restore"),
        danger: true,
      });

      if (confirmation) {
        setIsRestoring(true);
        try {
          const restoreRes = await adminApi.post(`/backups/${newBackup.id}/restore`);
          if (restoreRes.data.success) {
            notify(t("Database successfully restored!"), "success");
            setSelectedBackupId(null);
            await fetchBackups();
          }
        } catch (restoreErr) {
          console.error('Error restoring uploaded backup:', restoreErr);
          notify(t("Failed to restore backup"), "error");
        } finally {
          setIsRestoring(false);
        }
      }
    } catch (err: any) {
      console.error('Error uploading backup:', err);
      const msg = err.response?.data?.error || t("Failed to upload backup");
      notify(msg, "error");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUploadFile(file);
  };

  const handleDownload = async (id: number, date: string, type: string) => {
    try {
      const response = await adminApi.get(`/backups/${id}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const extension = type === 'sqlite' ? 'db' : type === 'mongodb' ? 'json' : 'sql';
      const fileName = `backup_${type}_${date.replace(/[: ]/g, '_')}.${extension}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading backup:', err);
      notify(t("Failed to download backup"), "error");
    }
  };

  const selectedBackup = backups.find(b => b.id === selectedBackupId);

  // Tylko pierwsze wczytanie - odświeżenie po utworzeniu / usunięciu kopii zostawia listę
  // na ekranie (wcześniej cały widok znikał na czas ładowania)
  if (loading && backups.length === 0) {
    return <div className="admin-empty">{t("Ładowanie...")}</div>;
  }

  return (
    <>
      <div className="admin-page__header">
        <div>
          <h2>{t("Backups")}</h2>
          <p>{t("Create, restore, and manage database backups")}</p>
          <p className="backup-scope">{t("SQLite: prices and panel data · MongoDB: configurations and carts")}</p>
        </div>
        <div className="admin-page__actions">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".db,.json,.sqlite,.sqlite3"
            onChange={handleFileInputChange}
          />
          <button
            className="admin-btn admin-btn--secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isCreating || isRestoring || isUploading}
          >
            {isUploading ? <><Spinner /> {t("Uploading...")}</> : <><Upload size={16} /> {t("Upload backup")}</>}
          </button>
          <button
            className="admin-btn"
            onClick={handleCreateBackup}
            disabled={isCreating || isRestoring || isUploading}
          >
            {isCreating ? <><Spinner /> {t("Creating...")}</> : <><Archive size={16} /> {t("Create backup")}</>}
          </button>
        </div>
      </div>

      {error && <div className="admin-alert admin-alert--error">{error}</div>}

      <div
        className={`admin-card ${isDragOver ? 'is-drag-over' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {backups.length === 0 ? (
          <div className="admin-empty">
            <p>{t("No backups found. Create your first backup or upload an existing backup file.")}</p>
            <div className="backup-empty-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '16px' }}>
              <button
                className="admin-btn admin-btn--secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isCreating || isRestoring}
              >
                {isUploading ? <><Spinner /> {t("Uploading...")}</> : <><Upload size={16} /> {t("Upload backup")}</>}
              </button>
              <button
                className="admin-btn"
                onClick={handleCreateBackup}
                disabled={isUploading || isCreating || isRestoring}
              >
                {isCreating ? <><Spinner /> {t("Creating...")}</> : <><Archive size={16} /> {t("Create backup")}</>}
              </button>
            </div>
          </div>
        ) : (
          <div className="backup-table-wrap">
            <table className="admin-table backup-table">
              <thead>
                <tr>
                  <th className="backup-col-select"><span className="sr-only">{t("Selected")}</span></th>
                  <th>{t("Data utworzenia")}</th>
                  <th>{t("Size")}</th>
                  <th>{t("Created by")}</th>
                  <th className="backup-col-actions">{t("Aktionen")}</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup) => (
                  <tr
                    key={backup.id}
                    className={selectedBackupId === backup.id ? 'is-selected' : undefined}
                    onClick={() => setSelectedBackupId(backup.id)}
                  >
                    <td className="backup-col-select">
                      <input
                        type="radio"
                        name="selected-backup"
                        checked={selectedBackupId === backup.id}
                        onChange={() => setSelectedBackupId(backup.id)}
                        aria-label={formatDate(backup.created_at)}
                      />
                    </td>
                    <td>
                      {formatDate(backup.created_at)}{' '}
                      <span className="admin-code">{typeLabel(backup.database_type)}</span>
                    </td>
                    <td>{formatSize(backup.size_bytes)}</td>
                    <td>{backup.created_by}</td>
                    <td className="backup-col-actions">
                      <button
                        className="admin-btn admin-btn--ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(backup.id, backup.created_at, backup.database_type);
                        }}
                      >
                        <Download size={15} /> {t("Pobierz")}
                      </button>
                      <button
                        className="admin-btn admin-btn--danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(backup.id);
                        }}
                      >
                        <Trash2 size={15} /> {t("Usuń")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="backup-restore">
          <span className="backup-restore__info">
            {selectedBackup
              ? `${t("Selected")}: ${formatDate(selectedBackup.created_at)} (${typeLabel(selectedBackup.database_type)})`
              : t('Please select a backup from the list to restore.')}
          </span>
          <button
            className="admin-btn admin-btn--danger"
            onClick={handleRestore}
            disabled={!selectedBackupId || isRestoring}
          >
            {isRestoring ? (
              <><Spinner /> {t("Restoring...")}</>
            ) : (
              <><RotateCcw size={16} /> {t('Restore Selected Backup')}</>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

export default Backups;
